import {NgModule, Pipe, PipeTransform} from '@angular/core';
import {CommonModule} from '@angular/common';
import {prettifyPhone, round} from '@nxt/model-core';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';

@Pipe({name: 'filterBy'})
export class FilterByPipe implements PipeTransform {
    transform(records: any[], config?: any): any {
        let properties: string[] = config.properties;
        let term: string = config.term?.toLowerCase();
        if (records && term) {
            return records.filter((item, n) => {
                if (!term) {
                    return true;
                } else if (!properties?.length) {
                    if (typeof item === 'string') {
                        if (item.toLowerCase().match(term)) {
                            return true;
                        } else {
                            return false;
                        }
                    } else {
                        return true;
                    }
                } else {
                    for (let p of properties) {
                        if (p.match(/\./)) {
                            let splits: string[] = p.split('.');
                            let passed: string = splits.reduce((result,part)=>{
                                if (result[part]) {
                                    result = result[part];
                                } else {
                                    result = null;
                                }
                                return result;
                            },item);
                            if (passed?.toLowerCase().match(term)) {
                                return true;
                            }

                        } else if (item[p]?.toLowerCase().match(term)) {
                            return true;
                        }
                    }
                    return false;
                }
            });
        } else {
            return records;
        }
    }
}

@Pipe({ name: 'orderBy' })
export class OrderByPipe implements PipeTransform {
    transform(records: any[], args?: any): any {
        if (records && args?.property) {
            args.direction = args.direction === undefined ? 1 : args.direction;
            let numeric: boolean = args.numeric || false;

            return records.sort((a, b) => {
                if (a && b) {
                    let splits: string[] = args.property.split('.');
                    a = splits.reduce((result,part)=>{
                        if (result[part]) {
                            result = result[part];
                        } else {
                            result = null;
                        }
                        return result;
                    },a);
                    b = splits.reduce((result,part)=>{
                        if (result[part]) {
                            result = result[part];
                        } else {
                            result = null;
                        }
                        return result;
                    },b);

                    a = (numeric || isNaN(a) === false) ? Number(a) : a;
                    b = (numeric || isNaN(b) === false) ? Number(b) : b;

                    if (a < b) {
                        return -1 * args.direction;
                    } else if (a > b) {
                        return 1 * args.direction;
                    } else {
                        return 0;
                    }
                } else {
                    return 0;
                }
            });
        }
    }
}

@Pipe({name: 'phone'})
export class PhoneFormatPipe implements PipeTransform {
    transform(number) {
        return prettifyPhone(number);
    }
}

@Pipe({name: 'short_number'})
export class ShortNumberPipe implements PipeTransform {
    transform(n: number | string): string | null {
        if (n && !isNaN(Number(n))) {
            if (typeof n === 'string') {
                n = Number(n);
            }
            if (n < 1000) {
                return n.toString();
            } else if (n < 1000000) {
                return `${round(n/1000, 1)}K`;
            } else {
                return `${round(n/1000000, 2)}M`;
            }
        } else {
            return '0';
        }
    }
}

@Pipe({
    name: 'htmlContentStyles',
    standalone: true
})
export class HtmlContentStylesPipe implements PipeTransform {
    transform(additionalClasses?: string): string {
        const baseClasses = `
      [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4
      [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4
      [&_p]:mb-4
      [&_a]:text-blue-600 [&_a]:underline
      [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-2
      [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-2
      [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic`;

        return additionalClasses ? `${additionalClasses} ${baseClasses}` : baseClasses;
    }
}

@Pipe({standalone: true, name: 'filePreviewUrl'})
export class FilePreviewUrlPipe implements PipeTransform {
    constructor(private sanitizer: DomSanitizer) {}

    transform(url: string, type: string): { safeUrl: SafeResourceUrl, previewType: 'img' | 'iframe' | 'video' | null } {
        if (!url || !type) return { safeUrl: '', previewType: null };

        const docTypes = [
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-powerpoint',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        ];

        let previewType: 'img' | 'iframe' | 'video' | null = null;
        let safeUrl: SafeResourceUrl;

        if (type.startsWith('image/')) {
            previewType = 'img';
            safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        } else if (type === 'application/pdf') {
            previewType = 'iframe';
            safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        } else if (type === 'video/mp4') {
            previewType = 'video';
            safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        } else if (docTypes.includes(type)) {
            previewType = 'iframe';
            const base = `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;
            safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(base + `&cb=${Date.now()}`);
        } else {
            previewType = null;
            safeUrl = '';
        }

        return { safeUrl, previewType };
    }
}


@Pipe({
    name: 'dateAgo',
    pure: true
})
export class DateAgoPipe implements PipeTransform {

    transform(value: any, args?: any): any {
        if (value) {
            const seconds = Math.floor((+new Date() - +new Date(value)) / 1000);
            if (seconds < 29) // less than 30 seconds ago will show as 'Just now'
                return 'Just now';
            const intervals: { [key: string]: number } = {
                'year': 31536000,
                'month': 2592000,
                'week': 604800,
                'day': 86400,
                'hour': 3600,
                'minute': 60,
                'second': 1
            };
            let counter;
            for (const i in intervals) {
                counter = Math.floor(seconds / intervals[i]);
                if (counter > 0)
                    if (counter === 1) {
                        return counter + ' ' + i + ' ago'; // singular (1 day ago)
                    } else {
                        return counter + ' ' + i + 's ago'; // plural (2 days ago)
                    }
            }
        }
        return value;
    }

}
@Pipe({
    standalone: true,
    name: 'abs'
})
export class AbsPipe implements PipeTransform {
    transform(value: number): number {
        return Math.abs(value);
    }
}

@NgModule({
    imports: [CommonModule],
    declarations: [FilterByPipe, OrderByPipe, PhoneFormatPipe, ShortNumberPipe, DateAgoPipe],
    exports: [FilterByPipe, OrderByPipe, PhoneFormatPipe, ShortNumberPipe, DateAgoPipe]
})
export class PipesModule {

}
